<template>
  <BittsLayout :is-page="true" class="overflow-y-auto mb-28">
    <PageTitle
      title="Lists"
      subtitle="Compare data with your partners to find strategic opportunities to work together"
    >
      <template #action>
        <CreateNewList
          ref="createNewListRef"
          v-if="canCreateList"
          @show-create-folder="onShowCreateFolderModal"
        />
      </template>
    </PageTitle>
    <div class="lists-page__table-container">
      <ListsTable
        ref="listsTableRef"
        :is-on-lists-page="true"
        :has-write-permissions="canCreateList"
        :trial-over="Boolean(isTrialOver)"
        :checked-lists="checkedLists"
        @show-create-folder-modal="onShowCreateFolderModal"
        @update:checked-lists="onUpdateCheckedLists"
        @create-list="onCreateListType()"
      />
    </div>
    <RouterViewWrapper />
    <CreateFolderModal
      v-if="showCreateFolderModal"
      v-model:visible="showCreateFolderModal"
      :report-ids="checkedLists"
      @closed="onCloseCreateFolderModal"
    />
  </BittsLayout>
</template>
<script setup lang="ts">
import { BittsLayout } from '@crossbeam/bitts';

import { useHead } from '@unhead/vue';
import { storeToRefs } from 'pinia';
import { computed, provide, reactive, ref, toRefs } from 'vue';

import PageTitle from '@/components/PageTitle.vue';
import CreateFolderModal from '@/components/reports/CreateFolderModal.vue';
import CreateNewList from '@/components/reports/CreateNewList.vue';
import ListsTable from '@/components/reports/ListsTable.vue';

import useAuth from '@/composables/useAuth';
import { useConnectorTrialEndDate } from '@/composables/useConnectorTrialEndDate';
import { ProvidedReportState } from '@/injectionKeys/lists';
import { useRootStore } from '@/stores/RootStore';

useHead({ title: 'Lists' });

const { hasScope } = useAuth();
const { hasSalesOnlyRole } = storeToRefs(useRootStore());
const canCreateList = computed(
  () => hasScope('write:reports') && !hasSalesOnlyRole.value,
);
const { isTrialOver } = useConnectorTrialEndDate();

const showCreateFolderModal = ref(false);

provide(ProvidedReportState, toRefs(reactive({ report: null })));

const checkedLists = ref<string[]>([]);
const listsTableRef = ref<InstanceType<typeof ListsTable> | null>(null);

function onShowCreateFolderModal(lists: string[] = []) {
  showCreateFolderModal.value = true;
  checkedLists.value = lists;
}
function onCloseCreateFolderModal() {
  showCreateFolderModal.value = false;
  checkedLists.value = [];
  listsTableRef.value?.clearSelection();
}

function onUpdateCheckedLists(lists: string[]) {
  checkedLists.value = lists;
}

const createNewListRef = ref<InstanceType<typeof CreateNewList> | null>(null);
function onCreateListType() {
  if (!createNewListRef.value) return;
  createNewListRef.value.createListType();
}

defineExpose({ checkedLists, onShowCreateFolderModal, onUpdateCheckedLists });
</script>

<style scoped lang="pcss">
@reference "../css/theme.css";
:deep(.ag-header-cell),
:deep(.ag-cell),
:deep(.reports-table-cell__wrapper) {
  @apply px-8;
}

:deep(.bitts-cta.bitts-cta-x-small) {
  @apply leading-4 px-0;
}

.lists-page__table-container {
  @apply relative h-full;
}

.lists-page__billing-cta {
  @apply mb-24;
}
</style>
